import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { MetaBadge } from './MetaIcon';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { GoogleIcon } from './GoogleIcon';
import { platformMeta } from './leadsMeta';
import { AdPlatform } from '../../../services/marketingLeads';

export type IntegrationDialogMode = 'connect' | 'disconnect';

interface IntegrationDialogProps {
  open: boolean;
  mode: IntegrationDialogMode;
  platform: 'META' | 'GOOGLE';
  busy: boolean;
  accountId?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export const IntegrationDialog: React.FC<IntegrationDialogProps> = ({
  open,
  mode,
  platform,
  busy,
  accountId,
  onCancel,
  onConfirm,
}) => {
  const meta = platformMeta[platform as AdPlatform];
  const isDisconnect = mode === 'disconnect';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: dashboardTokens.cardBg,
          border: `1px solid ${dashboardTokens.border}`,
          borderRadius: 3,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {!isDisconnect && platform === 'META' ? (
            <MetaBadge size={38} />
          ) : (
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDisconnect ? '#ef444422' : '#fff',
                flexShrink: 0,
              }}
            >
              {isDisconnect ? <LinkOffIcon sx={{ fontSize: 20, color: '#ef4444' }} /> : <GoogleIcon size={20} />}
            </Box>
          )}
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
            {isDisconnect ? `Disconnect ${meta.label}?` : `Connect ${meta.label}`}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {isDisconnect ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.6 }}>
              This removes the stored access token and stops the daily sync. Leads and campaign
              history already captured are kept — nothing is deleted.
            </Typography>
            {accountId && (
              <Box
                sx={{
                  bgcolor: '#1c1c1c',
                  border: `1px solid ${dashboardTokens.border}`,
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography sx={{ fontSize: 11, color: dashboardTokens.textSecondary }}>
                  Ad account
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#e2e8f0', fontFamily: 'monospace' }}>
                  {accountId}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontSize: 13, color: dashboardTokens.textMuted, lineHeight: 1.6 }}>
              You can reconnect at any time. New leads will not arrive while disconnected.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.6 }}>
              You'll be taken to {meta.label} to authorise access. Nouvak will read your campaign
              performance and receive new leads from your connected Page.
            </Typography>
            <Typography sx={{ fontSize: 13, color: dashboardTokens.textMuted, lineHeight: 1.6 }}>
              Sign in with an account that manages the ad account and the Page your lead forms
              live on.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onCancel}
          disabled={busy}
          sx={{ textTransform: 'none', color: '#94a3b8', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          disableElevation
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            bgcolor: isDisconnect ? '#ef4444' : meta.color,
            '&:hover': { bgcolor: isDisconnect ? '#dc2626' : meta.color, filter: 'brightness(1.1)' },
          }}
        >
          {busy
            ? isDisconnect
              ? 'Disconnecting…'
              : 'Opening…'
            : isDisconnect
              ? 'Disconnect'
              : `Continue to ${meta.label}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
